(() => {
  if (customElements.get('hv-welcome')) return;

  const SUB_KEY = 'hv_welcome_subscribed';
  const OLD_KEY = 'hv_welcome';
  const STATE_KEY = 'hv_welcome_state';
  const TEASER_KEY = 'hv_welcome_teaser';
  const START_KEY = 'hv_welcome_start';
  const REVISION = '2026-07-15';
  const EASE = 'cubic-bezier(.45, 0, .55, 1)';
  const MAX_BREATH = 14000;
  const MIN_ON_PAGE = 4000;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const cookie = {
    get(k) {
      try {
        const m = document.cookie.match(new RegExp('(?:^|; )' + k + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
      } catch (e) {
        return null;
      }
    },
    set(k, v, maxAge) {
      try {
        document.cookie = `${k}=${encodeURIComponent(v)}; path=/; SameSite=Lax${maxAge ? '; max-age=' + maxAge : ''}`;
      } catch (e) {}
    },
  };

  // This visit: sessionStorage, with a session cookie if storage is blocked.
  const visit = {
    get(k) {
      try {
        const v = window.sessionStorage.getItem(k);
        if (v !== null) return v;
      } catch (e) {}
      return cookie.get(k);
    },
    set(k, v) {
      try {
        window.sessionStorage.setItem(k, v);
        return;
      } catch (e) {}
      cookie.set(k, v);
    },
  };

  // Every visit: localStorage, with a one-year cookie if storage is blocked.
  const always = {
    get(k) {
      try {
        const v = window.localStorage.getItem(k);
        if (v !== null) return v;
      } catch (e) {}
      return cookie.get(k);
    },
    set(k, v) {
      try {
        window.localStorage.setItem(k, v);
        return;
      } catch (e) {}
      cookie.set(k, v, 31536000);
    },
  };

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HvWelcome extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.hv-pop');
      if (!this.dialog) return;
      this.teaser = this.querySelector('[data-pop-teaser]');
      this.form = this.querySelector('form');
      this.orb = this.querySelector('[data-pop-orb]');
      this.test = this.dataset.test === 'true';
      this.design = this.dataset.design === 'true';
      this.remember = !this.test && !this.design;
      this.need = '';
      this.timers = [];
      this.bind();

      if (this.test) {
        this.later(() => this.open(this.design ? this.dataset.editorStep || 'breath' : 'breath'), 400);
        return;
      }
      if (this.design) return;
      if (this.isSubscribed()) return;

      const state = visit.get(STATE_KEY);
      if (state === 'done') return;
      if (state) {
        // Seen or closed earlier in this visit: never open by itself again, only the teaser.
        if (state === 'shown') visit.set(STATE_KEY, 'closed');
        this.showTeaser();
        return;
      }
      this.arm();
    }

    disconnectedCallback() {
      this.disarm();
      this.clearTimers();
      this.stopBreath();
      clearTimeout(this.needTimer);
      ['select', 'load'].forEach((n) => document.removeEventListener(`shopify:section:${n}`, this.onSelect));
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.dialog && this.dialog.open) document.documentElement.classList.remove('hv-lock');
    }

    isSubscribed() {
      if (window.hvSettings && window.hvSettings.customerSubscribed) return true;
      return always.get(SUB_KEY) === '1' || always.get(OLD_KEY) === 'subscribed';
    }

    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        if (t === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (!inside) this.dismiss();
          return;
        }
        if (t.closest('[data-pop-close]')) return this.dismiss();
        if (t.closest('[data-pop-skip]')) return this.toOffer();
        if (t.closest('[data-pop-reopen]')) return this.open('teaser');
        if (t.closest('[data-pop-teaser-x]')) return this.hideTeaser();
        const choice = t.closest('.hv-pop__choice');
        if (choice) return this.choose(choice);
        const copy = t.closest('[data-pop-copy]');
        if (copy) return this.copy(copy);
        if (t.closest('[data-pop-shop]')) this.flushNeed();
      });

      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.dismiss();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('keydown', (e) => this.trap(e));

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.submit(e));
        const input = this.form.querySelector('input[type="email"]');
        if (input)
          input.addEventListener('input', () => {
            if (input.hasAttribute('aria-invalid')) this.clearError();
          });
      }

      const mine = (e) => this.isConnected && e.detail && e.detail.sectionId === this.dataset.section;
      this.onSelect = (e) => {
        if (mine(e)) this.open(this.dataset.editorStep || 'breath');
      };
      this.onBlockSelect = (e) => {
        if (mine(e)) this.open('success');
      };
      this.onDeselect = (e) => {
        if (mine(e) && this.dialog.open) {
          this.quiet = true;
          this.dismiss();
        }
      };
      ['select', 'load'].forEach((n) => document.addEventListener(`shopify:section:${n}`, this.onSelect));
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* When it shows: after the delay on site, or after scrolling a page, or (desktop) exit intent. */
    arm() {
      let start = Number(visit.get(START_KEY));
      if (!start) {
        start = Date.now();
        visit.set(START_KEY, String(start));
      }
      const wait = Math.max(Number(this.dataset.delay || 15) * 1000 - (Date.now() - start), MIN_ON_PAGE);
      this.timer = setTimeout(() => this.trigger('breath'), wait);

      const limit = Number(this.dataset.scroll || 50) / 100;
      this.onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 200 && window.scrollY / max >= limit) this.trigger('breath');
      };
      // Wait a moment so a restored scroll position on refresh never opens it on load.
      this.scrollArm = setTimeout(() => window.addEventListener('scroll', this.onScroll, { passive: true }), 1500);

      if (window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 861px)').matches) {
        this.onLeave = (e) => {
          if (!e.relatedTarget && e.clientY <= 0) this.trigger('exit');
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    disarm() {
      clearTimeout(this.timer);
      clearTimeout(this.scrollArm);
      clearTimeout(this.retry);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    busy() {
      const html = document.documentElement;
      return (
        html.classList.contains('hv-cart-open') ||
        html.classList.contains('hv-menu-open') ||
        !!document.querySelector('dialog[open]') ||
        document.visibilityState === 'hidden'
      );
    }

    trigger(mode) {
      if (this.triggered) return;
      if (this.busy()) {
        if (mode === 'exit') return;
        clearTimeout(this.retry);
        this.retry = setTimeout(() => this.trigger(mode), 3000);
        return;
      }
      this.triggered = true;
      this.disarm();
      this.open(mode);
    }

    /* mode: breath (step 1), offer / teaser / exit (step 2), success (step 3) */
    open(mode) {
      if (!this.isConnected) return;
      const step = mode === 'breath' ? 1 : mode === 'success' ? 3 : 2;
      const exit = mode === 'exit';

      const title = this.querySelector('[data-pop-title]');
      if (title) title.textContent = exit && title.dataset.exit ? title.dataset.exit : title.dataset.default;
      const sub = this.querySelector('[data-pop-sub]');
      if (sub) {
        const text = exit ? sub.dataset.exit : sub.dataset.default;
        sub.textContent = text || '';
        sub.hidden = !text;
      }

      this.stopBreath();
      this.clearTimers();
      if (this.teaser) this.teaser.hidden = true;
      this.dialog.classList.add('is-instant');
      this.setStep(step);

      if (!this.dialog.open) {
        this._return = document.activeElement;
        try {
          this.dialog.showModal();
        } catch (e) {
          this.dialog.setAttribute('open', '');
        }
        this.isOpen = true;
        document.documentElement.classList.add('hv-lock');
        if (this.remember && !this.subscribed) visit.set(STATE_KEY, 'shown');
      }
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.remove('is-instant')));
      this.focusStep(step);
      if (step === 1) this.breathe();
    }

    setStep(n) {
      this.step = n;
      this.dialog.dataset.step = String(n);
      this.querySelectorAll('[data-pop-step]').forEach((el) => {
        el.hidden = el.dataset.popStep !== String(n);
      });
      this.dialog.setAttribute('aria-labelledby', `HvPopT${n}-${this.dataset.section}`);
    }

    focusStep(n) {
      const target = n === 1 ? this.querySelector('[data-pop-skip]') : this.querySelector(`[data-pop-step="${n}"] .hv-pop__title`);
      if (target) target.focus({ preventScroll: true });
    }

    /* Step 1: one breath, then the offer. Never longer than 14 seconds, always skippable. */
    breathe() {
      let inMs = Number(this.dataset.inhale) * 1000 || 4000;
      let outMs = Number(this.dataset.exhale) * 1000 || 6000;
      const total = inMs + outMs;
      if (total > MAX_BREATH) {
        inMs *= MAX_BREATH / total;
        outMs *= MAX_BREATH / total;
      }
      const d = this.dialog;
      if (reduceMotion()) {
        d.dataset.breath = 'out';
        this.later(() => this.toOffer(), 1500);
        return;
      }
      d.dataset.breath = 'in';
      if (this.orb && this.orb.animate) {
        this.anim = this.orb.animate(
          [
            { transform: 'scale(.6)', easing: EASE },
            { transform: 'scale(1)', offset: inMs / (inMs + outMs), easing: EASE },
            { transform: 'scale(.6)' },
          ],
          { duration: inMs + outMs }
        );
      }
      this.later(() => {
        d.dataset.breath = 'out';
      }, inMs);
      this.later(() => this.toOffer(), inMs + outMs);
    }

    stopBreath(smooth) {
      const a = this.anim;
      this.anim = null;
      if (a) {
        if (smooth) {
          try {
            a.commitStyles();
          } catch (e) {}
        }
        a.cancel();
        if (smooth && this.orb) {
          void this.orb.offsetWidth;
          this.orb.style.transform = '';
        }
      }
      if (this.dialog) delete this.dialog.dataset.breath;
    }

    toOffer() {
      if (this.step !== 1 || !this.dialog.open) return;
      this.stopBreath(true);
      this.clearTimers();
      this.setStep(2);
      this.focusStep(2);
    }

    later(fn, ms) {
      this.timers.push(setTimeout(fn, ms));
    }

    clearTimers() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
    }

    trap(e) {
      if (e.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter((el) => !el.closest('[hidden]') && el.getClientRects().length);
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) {
        e.preventDefault();
        items[items.length - 1].focus();
      } else if (!e.shiftKey && i === items.length - 1) {
        e.preventDefault();
        items[0].focus();
      }
    }

    dismiss() {
      if (this.dialog.open) this.dialog.close();
      this.afterClose();
    }

    // Runs straight after our own close, and from the dialog's close event for any other close.
    afterClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.stopBreath();
      this.clearTimers();
      this.flushNeed();
      document.documentElement.classList.remove('hv-lock');
      const subscribed = this.subscribed || this.isSubscribed();
      if (this.remember && !subscribed) visit.set(STATE_KEY, 'closed');
      if (!subscribed && !this.quiet) this.showTeaser();
      this.quiet = false;
      const back = this._return;
      this._return = null;
      if (back && back !== document.body && back.focus && document.contains(back)) back.focus({ preventScroll: true });
    }

    showTeaser() {
      if (!this.teaser || this.teaserOff) return;
      if (this.remember && visit.get(TEASER_KEY) === 'off') return;
      this.teaser.hidden = false;
    }

    hideTeaser() {
      this.teaserOff = true;
      if (this.teaser) this.teaser.hidden = true;
      if (this.remember) visit.set(TEASER_KEY, 'off');
    }

    klaviyo() {
      const key = this.dataset.kKey;
      const list = this.dataset.kList;
      if (key && list) return { key, list };
      const g = (window.hvSettings && window.hvSettings.klaviyo) || {};
      return g.key && g.list ? { key: g.key, list: g.list } : null;
    }

    showError(text) {
      const input = this.form.querySelector('input[type="email"]');
      const msg = this.querySelector('[data-pop-msg]');
      if (input) input.setAttribute('aria-invalid', 'true');
      if (msg) msg.textContent = text || '';
    }

    clearError() {
      const input = this.form.querySelector('input[type="email"]');
      const msg = this.querySelector('[data-pop-msg]');
      if (input) input.removeAttribute('aria-invalid');
      if (msg) msg.textContent = '';
    }

    /* Step 2: email to Klaviyo (or Shopify customer form), then step 3. */
    async submit(e) {
      e.preventDefault();
      if (this.sending) return;
      const HV = window.HV;
      const input = this.form.querySelector('input[type="email"]');
      const msg = this.querySelector('[data-pop-msg]');
      const email = (input.value || '').trim();
      if (!HV) {
        this.showError(msg.dataset.error);
        return;
      }
      if (!HV.isEmail(email)) {
        this.showError(msg.dataset.invalid);
        input.focus();
        return;
      }
      this.clearError();
      const btn = this.form.querySelector('[type="submit"]');
      this.sending = true;
      btn.setAttribute('aria-disabled', 'true');
      const result = await HV.subscribe(this.form, {
        source: 'welcome_popup',
        properties: { signup_source: 'welcome_popup' },
        klaviyo: this.klaviyo(),
      });
      this.sending = false;
      btn.removeAttribute('aria-disabled');
      if (result.ok) {
        this.subscribed = true;
        this.email = email;
        if (this.remember) {
          always.set(SUB_KEY, '1');
          visit.set(STATE_KEY, 'done');
        }
        this.setStep(3);
        this.focusStep(3);
      } else if (result.reason === 'invalid') {
        this.showError(msg.dataset.invalid);
        input.focus();
      } else if (result.reason !== 'challenge') {
        this.showError(msg.dataset.error);
      }
    }

    /* Step 3: the optional question. */
    choose(btn) {
      const on = btn.getAttribute('aria-pressed') !== 'true';
      this.querySelectorAll('.hv-pop__choice').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      const shop = this.querySelector('[data-pop-shop]');
      if (on) {
        btn.setAttribute('aria-pressed', 'true');
        this.need = btn.dataset.need || '';
      } else {
        this.need = '';
      }
      if (shop) {
        shop.href = (on && btn.dataset.link) || shop.dataset.fallbackLink;
        shop.textContent = (on && btn.dataset.cta) || shop.dataset.fallback;
      }
      clearTimeout(this.needTimer);
      if (this.need) this.needTimer = setTimeout(() => this.flushNeed(), 800);
    }

    flushNeed() {
      clearTimeout(this.needTimer);
      this.needTimer = null;
      const need = this.need;
      if (!need || !this.email || need === this.sentNeed) return;
      this.sentNeed = need;
      const k = this.klaviyo();
      try {
        if (k) {
          fetch(`https://a.klaviyo.com/client/profiles?company_id=${encodeURIComponent(k.key)}`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION },
            body: JSON.stringify({ data: { type: 'profile', attributes: { email: this.email, properties: { evening_need: need } } } }),
          }).catch(() => {});
        } else if (this.form) {
          const fd = new FormData(this.form);
          fd.set('contact[email]', this.email);
          fd.set('contact[tags]', `newsletter, welcome-popup, need-${need}`);
          fetch(this.form.action, { method: 'POST', body: fd, keepalive: true, headers: { Accept: 'text/html' } }).catch(() => {});
        }
      } catch (e) {}
    }

    async copy(btn) {
      const code = btn.dataset.code || '';
      try {
        await navigator.clipboard.writeText(code);
      } catch (e) {
        const t = document.createElement('textarea');
        t.value = code;
        t.setAttribute('readonly', '');
        t.style.position = 'fixed';
        t.style.opacity = '0';
        this.dialog.appendChild(t);
        t.select();
        try {
          document.execCommand('copy');
        } catch (e2) {}
        t.remove();
        btn.focus();
      }
      btn.textContent = btn.dataset.copied;
    }
  }

  customElements.define('hv-welcome', HvWelcome);
})();
